import React, { useState, useRef } from "react";
import { Card, CardContent, Typography, Box, Button, Checkbox, FormControlLabel } from "@mui/material";
import { useCphContext } from "../../hooks/useCphContext";
import { useCphDataContext } from "../../hooks/useCphDataContext";
import { useAddComment, useCloseIssue, useCompleteIssue } from "../../services/ApiHandlerService";
import Link from "@tiptap/extension-link";
import { LinkBubbleMenuHandler } from "mui-tiptap";
import {
    MenuButtonBold,
    MenuButtonEditLink,
    MenuButtonItalic,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    LinkBubbleMenu,
    RichTextEditor,
    type RichTextEditorRef,
} from "mui-tiptap";
import type { EditorOptions } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";

const AddCommentCard: React.FC = () => {
    const { isStaff } = useCphContext();
    const { selectedIssue } = useCphDataContext();
    const rteRef = useRef<RichTextEditorRef>(null);
    const [comment, setComment] = useState("");
    const { addComment } = useAddComment();
    const { completeIssue } = useCompleteIssue();
    const { closeIssue } = useCloseIssue();


    if (!selectedIssue) {
        return <Typography variant="body1">No issue selected.</Typography>;
    }

    const handleAddComment = async () => {
        const html = rteRef.current?.editor?.getHTML() ?? "";
        if (html.trim()) {
            await addComment(selectedIssue.issueKey!, html, staffOnlyFlag);
            setComment("");
            setStaffOnlyFlag(false);
            rteRef.current?.editor?.commands.setContent("<p>Add comment</p>");
        }
    };

    const handleMarkComplete = async () => {
        await completeIssue(selectedIssue.issueKey!);
    };

    const handleMarkClose = async () => {
        await closeIssue(selectedIssue.issueKey!);
    };

    const handleCancel = () => {
        setComment("");
        setStaffOnlyFlag(false);
        rteRef.current?.editor?.commands.setContent("");
    };

    const [staffOnlyFlag, setStaffOnlyFlag] = useState(false);

    return (
        <Card sx={{ width: '100%', height: '100%', maxWidth: '100%', mx: 0, px: 0, py: 0 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    <strong>Add Comment:</strong>
                </Typography>
                <RichTextEditor
                    ref={rteRef}
                    content=""
                    extensions={[
                        StarterKit,
                        Link.configure({
                            openOnClick: false,
                            autolink: true,
                            linkOnPaste: true,
                            HTMLAttributes: {
                                rel: 'noopener noreferrer',
                                target: '_blank',
                            },
                        }),
                        LinkBubbleMenuHandler,
                    ]}
                    renderControls={() => (
                        <>
                            <MenuControlsContainer>
                                <MenuDivider />
                                <MenuButtonBold />
                                <MenuButtonItalic />
                                <MenuButtonEditLink />
                            </MenuControlsContainer>
                            <LinkBubbleMenu />
                        </>
                    )}
                />
                {isStaff && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={staffOnlyFlag}
                                    onChange={(_, checked) => setStaffOnlyFlag(checked)}
                                />
                            }
                            label={<Typography variant="body2" sx={{ color: "#720522" }}><strong>Staff Only Comment</strong></Typography>}
                        />
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 3, my: 2 }}>
                    <Button variant="contained" size="small" onClick={handleAddComment}>
                        Add
                    </Button>

                    {isStaff && (
                        selectedIssue.status?.status === "In Progress"
                            || selectedIssue.status?.status === "New" ? (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleMarkComplete}
                            >
                                Mark Complete
                            </Button>
                        ) : (
                            <Button
                                sx={{ backgroundColor: "#720522" }}
                                variant="contained"
                                size="small"
                                onClick={handleMarkClose}
                                disabled={selectedIssue.status?.status === "Closed"}
                            >
                                Close Issue
                            </Button>
                        )
                    )}

                    <Button variant="outlined" color="secondary" size="small" onClick={handleCancel}>
                        Cancel
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default AddCommentCard;
